import { PenLine } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

/**
 * Opens the conversation's reply draft from above the composer, in words. It
 * is the visible way in beside the typed `.draft`, which a literal reader
 * misses: the dot reads as punctuation and the placeholder that teaches it
 * vanishes with the first keystroke.
 *
 * Right-aligned like the inquiry action row, and styled like its actions, so
 * the area above the composer reads as one set of controls. The two do not
 * meet: the chip is for the first reply, and those actions wait for one.
 * Sized to its content so the empty side of the row passes presses through to
 * the messages underneath.
 *
 * The pill is drawn small inside a pressable box 48px tall, rather than padded
 * with hit slop, because react-native-web ignores hit slop and this is the
 * main way into a draft for the readers least likely to land a small target.
 * The box carries the hover and press feedback, since the pill inside cannot
 * read the box's state.
 */
export function DraftReplyChip({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      testID="reply-draft-chip"
      className={twJoin(
        TOUCH_TARGET_MIN_H,
        'justify-center self-end web:cursor-pointer data-[hover=true]:opacity-90 data-[active=true]:opacity-70'
      )}
      {...getAccessibilityProps({ label, role: 'button' })}>
      <HStack className="h-7 items-center gap-1.5 rounded-full border border-secondary-400 bg-background-0 px-2.5">
        <Icon as={PenLine} size="xs" className="text-secondary-500" />
        <Text size="sm" className="font-medium text-secondary-500">
          {label}
        </Text>
      </HStack>
    </Pressable>
  );
}
